import { IDBPDatabase, openDB } from 'idb';

/**
 * Db is a generic async wrapper for IndexedDB using idb.
 *
 * @template T The type of value stored in the object store.
 *
 * Example usage:
 *   type User = { id: string; name: string };
 *   const userDb = new Db<User>('user-db', 'users');
 *   await userDb.set('user1', { id: 'user1', name: 'Alice' });
 *   const user = await userDb.get('user1'); // user is User | undefined
 */
class Db<T = unknown> {
  private dbPromise: Promise<IDBPDatabase<any>>;
  private storeName: string;

  /**
   * Creates a new Db instance.
   * @param dbName Name of the IndexedDB database.
   * @param storeName Name of the object store.
   * @param dbVersion Database version (default: 1).
   */
  constructor(dbName: string, storeName: string, dbVersion: number = 1) {
    this.storeName = storeName;
    this.dbPromise = openDB(dbName, dbVersion, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(storeName)) {
          db.createObjectStore(storeName);
        }
      },
    });
  }

  /**
   * Stores a value by key.
   * @param key The key to store the value under.
   * @param value The value to store.
   */
  async set(key: string, value: T): Promise<void> {
    const db = await this.dbPromise;
    await db.put(this.storeName, value, key);
  }

  /**
   * Retrieves a value by key. Optionally sets and returns a default value if not found.
   * @param key The key to retrieve.
   * @param defaultValue Optional default value to set and return if key is not found.
   * @returns The value or undefined.
   */
  async get(key: string | string[], defaultValue?: T | T[]): Promise<T | T[] | undefined> {
    if (Array.isArray(key)) {
      const values = await Promise.all(
        key.map(async (k, i) => {
          const result = await this.get(k, Array.isArray(defaultValue) ? defaultValue[i] : (defaultValue as T));
          return result as T;
        })
      );
      return values;
    }
    const db = await this.dbPromise;
    const value = await db.get(this.storeName, key);
    if (value !== undefined) return value;
    if (defaultValue !== undefined) {
      await this.set(key, defaultValue as T);
    }
    return defaultValue;
  }

  /**
   * Deletes a value by key.
   * @param key The key to delete.
   */
  async delete(key: string): Promise<void> {
    const db = await this.dbPromise;
    await db.delete(this.storeName, key);
  }

  /**
   * Clears all values in the object store.
   */
  async clear(): Promise<void> {
    const db = await this.dbPromise;
    await db.clear(this.storeName);
  }

  /**
   * Retrieves all keys in the object store.
   * @returns Array of keys.
   */
  async getAllKeys(): Promise<string[]> {
    const db = await this.dbPromise;
    return db.getAllKeys(this.storeName);
  }
}

// Uses any type for flexibility.
// This is not intended to be a specific storage type, more of a quick-storage utility for various data shapes.
export const Cache = new Db<any>('Cache', 'temp');
export default Db;
